import type { Notification } from '@/api/notifications';
import {
  NOTIFICATION_TRIGGER_ID,
  type NotificationTriggerId,
} from '@/lib/constants/notificationTriggers';
import { useSessionStore } from '@/store/sessionStore';

type LinkConfig = { link: string; label: string } | null;
/** A run of description text. `href` turns the run into a link — used where the
 *  only way to reach a file is inside the sentence, because informative
 *  notifications have no action button. */
type DescSegment = { text: string; bold: boolean; href?: string };

export function getActionLink(
  n: Notification,
  t: (key: string) => string,
): LinkConfig {
  const { notificationTriggerId: trigger, resourceId, payload: p } = n;
  const viewerCompanyId = useSessionStore.getState().user?.companyId;
  // The answer-request id — which supplier's response this is about. Every
  // questionnaire notification carries it under `questionnaire.requestId`
  // (the publisher sets it and the in-app row stores the raw message payload),
  // but `payload` is typed as an opaque bag, so narrow it here.
  const questionnaireRequestId = (
    p?.questionnaire as { requestId?: string } | undefined
  )?.requestId;
  // The supplier being asked. When the viewer *is* that company they're the
  // responder, not the reviewer, so they belong on the answer page — the
  // review page is the questionnaire owner's view. Mirrors the backend's own
  // `isRecipientAnsweringCompany` branch when it builds the email link.
  const isViewerAnsweringCompany =
    viewerCompanyId !== undefined && viewerCompanyId === p?.answeringCompanyId;
  const reviewLink = `/questionnaires/${resourceId}/review${
    questionnaireRequestId ? `?requestId=${questionnaireRequestId}` : ''
  }`;

  // Keyed by `NOTIFICATION_TRIGGER_ID` rather than raw strings so a mistyped id
  // fails the build instead of silently dropping the action button (DELTA-2317).
  const map: Partial<Record<NotificationTriggerId, LinkConfig>> = {
    // Certificates live in v2 at `/certifications/$id`, backed by the same
    // `/v3/claims/$id` resource the old `/claims/$id/view` page loaded, so
    // `resourceId` carries over unchanged (DELTA-2318).
    [NOTIFICATION_TRIGGER_ID.ownCertificatesExpiring]: {
      link: `/certifications/${resourceId}`,
      label: t('actionLabels.update'),
    },
    [NOTIFICATION_TRIGGER_ID.ownCertificatesExpired]: {
      link: `/certifications/${resourceId}`,
      label: t('actionLabels.update'),
    },
    [NOTIFICATION_TRIGGER_ID.claimRejected]: {
      link: `/certifications/${resourceId}`,
      label: t('actionLabels.review'),
    },
    // Someone asked to join the caller's company — reviewed from the
    // company's own "Join Requests" tab (Part 2 of the ask-to-join feature,
    // moved from the profile page, then again from there to Company
    // Settings). The "decided" sibling trigger is informative (no CTA): the
    // requester just gets told the outcome, there's nothing left to action.
    //
    // This trigger only ever reaches a company admin of the joined
    // company, so the logged-in viewer's own `companyId` IS that company's
    // id — which is also why the link needs no id of its own: Company
    // Settings is always the caller's own company.
    [NOTIFICATION_TRIGGER_ID.companyJoinRequestCreated]: {
      link: '/company-settings/join-requests',
      label: t('actionLabels.review'),
    },
    [NOTIFICATION_TRIGGER_ID.auditRequested]: {
      link: `/audits/${resourceId}/view`,
      label: t('actionLabels.respond'),
    },
    [NOTIFICATION_TRIGGER_ID.documentRequested]: {
      link: '/documents/my-documents',
      label: t('actionLabels.respond'),
    },
    [NOTIFICATION_TRIGGER_ID.questionnaireQuestionAdded]: {
      link: `/questionnaires/${resourceId}/answer`,
      label: t('actionLabels.answer'),
    },
    [NOTIFICATION_TRIGGER_ID.questionnaireInvitation]: {
      link: `/questionnaires/${resourceId}/answer?requestId=${resourceId}`,
      label: t('actionLabels.answer'),
    },
    // This trigger notifies whichever side did *not* comment, so the recipient
    // can be the supplier — send them to their own response instead of a review
    // page they cannot open. `resourceId` is the *questionnaire* id here, but the
    // answer route is keyed by the answer-request id, so the supplier's link has
    // to be built from `questionnaire.requestId` — the questionnaire id 404s.
    // Without a request id there is no response to open.
    [NOTIFICATION_TRIGGER_ID.questionnaireCommented]: isViewerAnsweringCompany
      ? questionnaireRequestId
        ? {
            link: `/questionnaires/${questionnaireRequestId}/answer`,
            label: t('actionLabels.viewComment'),
          }
        : null
      : { link: reviewLink, label: t('actionLabels.viewComment') },
    [NOTIFICATION_TRIGGER_ID.questionnaireResponseSubmitted]: {
      link: reviewLink,
      label: t('actionLabels.viewResponse'),
    },
    [NOTIFICATION_TRIGGER_ID.questionnaireReminder]: {
      link: `/questionnaires/${resourceId}/answer`,
      label: t('actionLabels.continue'),
    },
    [NOTIFICATION_TRIGGER_ID.questionnaireUnarchived]: {
      link: `/questionnaires/${resourceId}/answer`,
      label: t('actionLabels.continue'),
    },
    [NOTIFICATION_TRIGGER_ID.questionnaireDueDateChanged]: {
      link: `/questionnaires/${resourceId}/answer`,
      label: t('actionLabels.answer'),
    },
    [NOTIFICATION_TRIGGER_ID.questionnaireChangesRequested]: {
      link: `/questionnaires/${resourceId}/answer`,
      label: t('actionLabels.continue'),
    },
    // Questionnaire-level (no request id in the payload), so it lands on the
    // responses overview rather than any single response.
    [NOTIFICATION_TRIGGER_ID.ownQuestionnaireDueDateClosing]: {
      link: `/questionnaires/${resourceId}/summary`,
      label: t('actionLabels.goToQuestionnaire'),
    },
    [NOTIFICATION_TRIGGER_ID.requestQuestionnaireDueDateClosing]: {
      link: `/questionnaires/${resourceId}/answer`,
      label: t('actionLabels.respond'),
    },
    [NOTIFICATION_TRIGGER_ID.questionnaireExported]: {
      link: String(p?.downloadFileUrl ?? ''),
      label: t('actionLabels.download'),
    },
    [NOTIFICATION_TRIGGER_ID.supplyChainCollaborationRequested]: {
      link: `/supply-chain-mapping/supply-chains/${resourceId}/diagram`,
      label: t('actionLabels.respond'),
    },
    [NOTIFICATION_TRIGGER_ID.supplyChainCollaborationRequestReminded]: {
      link: `/supply-chain-mapping/supply-chains/${resourceId}/diagram`,
      label: t('actionLabels.respond'),
    },
    [NOTIFICATION_TRIGGER_ID.supplyChainCollaborationSubmitted]: {
      link: `/supply-chain-mapping/supply-chains/${resourceId}/diagram`,
      label: t('actionLabels.review'),
    },
    [NOTIFICATION_TRIGGER_ID.supplyChainCollaborationChangesRequested]: {
      link: `/supply-chain-mapping/supply-chains/${resourceId}/diagram`,
      label: t('actionLabels.viewFeedback'),
    },
    // Collaborative product mapping — `resourceId` is the collaborator id, which
    // is exactly what the v2 graph route (`/product-mapping/$cpmId`) is keyed by.
    [NOTIFICATION_TRIGGER_ID.cpmCollaboratorInvited]: {
      link: `/product-mapping/${resourceId}`,
      label: t('actionLabels.respond'),
    },
    [NOTIFICATION_TRIGGER_ID.cpmCollaboratorInReview]: {
      link: `/product-mapping/${resourceId}`,
      label: t('actionLabels.review'),
    },
    [NOTIFICATION_TRIGGER_ID.cpmCollaboratorChangesRequested]: {
      link: `/product-mapping/${resourceId}`,
      label: t('actionLabels.viewFeedback'),
    },
    [NOTIFICATION_TRIGGER_ID.cpmExportCompleted]: {
      link: String(p?.downloadFileUrl ?? ''),
      label: t('actionLabels.download'),
    },
    // `cpm-import-completed` is informative, so it never gets an action button —
    // its error report is an inline link in the description instead.
    [NOTIFICATION_TRIGGER_ID.reportsTracingOperationsExport]: {
      link: String(p?.downloadFileUrl ?? ''),
      label: t('actionLabels.download'),
    },
    [NOTIFICATION_TRIGGER_ID.reportsCertificatesComplianceExport]: {
      link: String(p?.downloadFileUrl ?? ''),
      label: t('actionLabels.download'),
    },
    // Same destination as the slcp-added email's "Go to Assessments" link.
    [NOTIFICATION_TRIGGER_ID.slcpAdded]: {
      link: '/slcp',
      label: t('actionLabels.goToAssessments'),
    },
    [NOTIFICATION_TRIGGER_ID.slcpAssessmentAccessRequested]: {
      link: `/slcp/assessments/${resourceId}`,
      label: t('actionLabels.reviewRequest'),
    },
    [NOTIFICATION_TRIGGER_ID.slcpAssessmentShared]: {
      link: `/slcp/assessments/${resourceId}`,
      label: t('actionLabels.viewAssessment'),
    },
    [NOTIFICATION_TRIGGER_ID.slcpAssessmentMissingAccessRequest]: {
      link: 'https://help.retraced.com/en/collections/15228461-slcp',
      label: t('actionLabels.learnMore'),
    },
    // `entity-scan-new-match` is intentionally absent: migration
    // 202605281000-drop-entity-scan-ai-v1 deleted the trigger and its
    // notifications, so nothing can carry that id. The kharon/uflpa triggers
    // below replaced it.
    [NOTIFICATION_TRIGGER_ID.entityScreeningKharonMatch]: {
      link: '/entity-screening',
      label: t('actionLabels.reviewNow'),
    },
    [NOTIFICATION_TRIGGER_ID.entityScreeningUflpaMatch]: {
      link: '/entity-screening',
      label: t('actionLabels.reviewNow'),
    },
    [NOTIFICATION_TRIGGER_ID.stylesExportCompleted]: {
      link: String(p?.fileUrl ?? ''),
      label: t('actionLabels.download'),
    },
    [NOTIFICATION_TRIGGER_ID.stylesExportFailed]: {
      link: '/my-styles',
      label: t('actionLabels.tryAgain'),
    },
    [NOTIFICATION_TRIGGER_ID.stylesBulkImportCompleted]: {
      link: '/my-styles',
      label: t('actionLabels.viewStyles'),
    },
    [NOTIFICATION_TRIGGER_ID.ordersExportCompleted]: {
      link: String(p?.fileUrl ?? ''),
      label: t('actionLabels.download'),
    },
    [NOTIFICATION_TRIGGER_ID.ordersExportFailed]: {
      link: '/orders-hub',
      label: t('actionLabels.tryAgain'),
    },
    // CMM order (PO) import finished — link to the error report when the import
    // had problems, otherwise to the orders list (CMMM-345).
    [NOTIFICATION_TRIGGER_ID.certifiedMaterialsPoImportCompleted]: {
      link:
        (p?.hasErrors || p?.hasWarnings) && p?.errorReportUrl
          ? String(p.errorReportUrl)
          : '/certified-materials',
      label:
        (p?.hasErrors || p?.hasWarnings) && p?.errorReportUrl
          ? t('actionLabels.viewReport')
          : t('actionLabels.viewOrders'),
    },
    [NOTIFICATION_TRIGGER_ID.capaUserAssigneeAdded]: {
      link: `/capa/actions/${p?.capaId}`,
      label: t('actionLabels.view'),
    },
    [NOTIFICATION_TRIGGER_ID.capaCollaboratorAdded]: {
      link: `/capa/actions/${p?.capaId}`,
      label: t('actionLabels.view'),
    },
    // `/capa/actions/…`, not `/capa/plans/…`: the payload carries `capaId` (an
    // action), and the plans route is keyed by a CAP id. Matches every other
    // `CAPA_*` entry here.
    [NOTIFICATION_TRIGGER_ID.capaResponsibleCompanyAdded]: {
      link: `/capa/actions/${p?.capaId}`,
      label: t('actionLabels.view'),
    },
    [NOTIFICATION_TRIGGER_ID.capaOverdue]: {
      link: `/capa/actions/${p?.capaId}`,
      label: t('actionLabels.viewCapa'),
    },
    [NOTIFICATION_TRIGGER_ID.capaStatusUpdateInReview]: {
      link: `/capa/actions/${p?.capaId}`,
      label: t('actionLabels.reviewCapa'),
    },
    [NOTIFICATION_TRIGGER_ID.capaStatusUpdateInReviewGeneral]: {
      link: `/capa/actions/${p?.capaId}`,
      label: t('actionLabels.viewCapa'),
    },
    [NOTIFICATION_TRIGGER_ID.capaStatusUpdateChangesRequested]: {
      link: `/capa/actions/${p?.capaId}`,
      label: t('actionLabels.reviewCapa'),
    },
    [NOTIFICATION_TRIGGER_ID.capaStatusUpdateChangesRequestedGeneral]: {
      link: `/capa/actions/${p?.capaId}`,
      label: t('actionLabels.viewCapa'),
    },
    [NOTIFICATION_TRIGGER_ID.capaStatusUpdateDoneGeneral]: {
      link: `/capa/actions/${p?.capaId}`,
      label: t('actionLabels.viewCapa'),
    },
    [NOTIFICATION_TRIGGER_ID.capaRemediationAdded]: {
      link: `/capa/actions/${p?.capaId}`,
      label: t('actionLabels.viewCapa'),
    },
    [NOTIFICATION_TRIGGER_ID.capaRemediationRemoved]: {
      link: `/capa/actions/${p?.capaId}`,
      label: t('actionLabels.viewCapa'),
    },
    [NOTIFICATION_TRIGGER_ID.capaCommentAdded]: {
      link: `/capa/actions/${p?.capaId}`,
      label: t('actionLabels.viewCapa'),
    },
    // The `_GENERAL` variant is the same event seen by a bystander (a
    // collaborator or watcher rather than the assignee), and its payload is
    // identical, so it points at the same place.
    [NOTIFICATION_TRIGGER_ID.capaCommentAddedGeneral]: {
      link: `/capa/actions/${p?.capaId}`,
      label: t('actionLabels.viewCapa'),
    },
    // Fires three days before the due date. `createdByCompany*` on these rows
    // is the RECIPIENT's own company — the job stamps whoever it is reminding
    // as the creator — so nothing here may render it.
    [NOTIFICATION_TRIGGER_ID.capaDueSoon]: {
      link: `/capa/actions/${p?.capaId}`,
      label: t('actionLabels.viewCapa'),
    },
    [NOTIFICATION_TRIGGER_ID.capUserAssigneeAdded]: {
      link: `/capa/plans/${p?.capId}`,
      label: t('actionLabels.view'),
    },
    [NOTIFICATION_TRIGGER_ID.capCollaboratorAdded]: {
      link: `/capa/plans/${p?.capId}`,
      label: t('actionLabels.view'),
    },
    [NOTIFICATION_TRIGGER_ID.capResponsibleCompanyAdded]: {
      link: `/capa/plans/${p?.capId}`,
      label: t('actionLabels.view'),
    },
    [NOTIFICATION_TRIGGER_ID.capOverdue]: {
      link: `/capa/plans/${p?.capId}`,
      label: t('actionLabels.viewCap'),
    },
    [NOTIFICATION_TRIGGER_ID.capCommentAdded]: {
      link: `/capa/plans/${p?.capId}`,
      label: t('actionLabels.viewCap'),
    },
    [NOTIFICATION_TRIGGER_ID.capCommentAddedGeneral]: {
      link: `/capa/plans/${p?.capId}`,
      label: t('actionLabels.viewCap'),
    },
    [NOTIFICATION_TRIGGER_ID.capDueSoon]: {
      link: `/capa/plans/${p?.capId}`,
      label: t('actionLabels.viewCap'),
    },
    [NOTIFICATION_TRIGGER_ID.certifiedMaterialsSupplierNewEvidenceRequested]: {
      link: '/certified-materials',
      label: t('actionLabels.viewOrders'),
    },
    [NOTIFICATION_TRIGGER_ID.certifiedMaterialsOrderSubmittedForReview]: {
      link: '/certified-materials',
      label: t('actionLabels.viewOrder'),
    },
    [NOTIFICATION_TRIGGER_ID.certifiedMaterialsDocumentRevisionRequested]: {
      link: '/certified-materials',
      label: t('actionLabels.viewOrder'),
    },
    // A supplier user was newly assigned to orders that are still collecting
    // evidence. One notification covers every order they gained, so it points at
    // the orders list rather than a single PO.
    [NOTIFICATION_TRIGGER_ID.certifiedMaterialsSupplierAssignedToOrders]: {
      link: '/certified-materials',
      label: t('actionLabels.viewOrders'),
    },
    [NOTIFICATION_TRIGGER_ID.certifiedMaterialsSupplierReminderSent]: {
      link: '/certified-materials',
      label: t('actionLabels.viewOrders'),
    },
  };

  // `trigger` is an opaque string off the wire; unknown ids fall through to null.
  return map[trigger as NotificationTriggerId] ?? null;
}

export function getDescriptionSegments(
  n: Notification,
  t: (key: string, params?: Record<string, string | number>) => string,
): DescSegment[] {
  const {
    notificationTriggerId: trigger,
    createdByCompanyName: co,
    resourceName: res,
    payload: p,
  } = n;

  function b(prefix: string, boldText: string, suffix = ''): DescSegment[] {
    return [
      { text: prefix ? `${prefix} ` : '', bold: false },
      { text: boldText, bold: true },
      { text: suffix ? ` ${suffix}` : '', bold: false },
    ];
  }

  // `products-imported` is the one trigger that nests its counters a level down.
  const upload = (p?.uploadStatus ?? {}) as Record<string, unknown>;

  // The amfori sync settles into COMPLETED / PARTIAL / FAILED; FAILED splits
  // again on the error code so the copy can say whether to fix credentials or
  // just wait for the next scheduled run.
  function amforiSync(): string {
    if (p?.status === 'FAILED') {
      const code = (p?.lastError as { code?: string } | undefined)?.code;
      if (code === 'AUTH_FAILED') {
        return t('triggers.amforiSyncReport.authFailed');
      }
      if (code === 'CREDENTIALS_REVOKED') {
        return t('triggers.amforiSyncReport.credentialsRevoked');
      }
      return t('triggers.amforiSyncReport.failed');
    }
    // The old frontend read `monitoringsPulled` / `findingsPulled`, neither of
    // which `AmforiSyncCountersSchema` defines — so its sentence always said
    // "0 audits and 0 findings". These are the fields the payload really has.
    const c = (p?.counters ?? {}) as Record<string, number>;
    const params = {
      audits: Number(c.auditsCreated ?? 0),
      findings: Number(c.findingsCreated ?? 0),
      errors: Number(c.errorsCount ?? 0),
    };
    return p?.status === 'COMPLETED'
      ? t('triggers.amforiSyncReport.completed', params)
      : t('triggers.amforiSyncReport.partial', params);
  }

  // Collaborative-product-mapping notifications are re-sent as reminders; the
  // reminder variant reuses the same sentence behind a bold "Reminder:".
  function cpm(prefixKey: string, suffixKey?: string): DescSegment[] {
    const segments = b(
      t(prefixKey, { co }),
      res,
      suffixKey ? t(suffixKey) : '',
    );
    return p?.isReminder
      ? [{ text: `${t('triggers.cpmReminder')} `, bold: true }, ...segments]
      : segments;
  }

  const map: Partial<Record<NotificationTriggerId, DescSegment[]>> = {
    [NOTIFICATION_TRIGGER_ID.ownCertificatesExpiring]: b(
      t('triggers.ownCertificatesExpiring.prefix'),
      res,
      t('triggers.ownCertificatesExpiring.suffix'),
    ),
    [NOTIFICATION_TRIGGER_ID.ownCertificatesExpired]: b(
      t('triggers.ownCertificatesExpired.prefix'),
      res,
      t('triggers.ownCertificatesExpired.suffix'),
    ),
    [NOTIFICATION_TRIGGER_ID.claimRejected]: b(
      t('triggers.claimRejected.prefix'),
      res,
      t('triggers.claimRejected.suffix'),
    ),
    // `res` (resourceName) carries the requesting user's full name — the
    // "resource" a join-request notification is about.
    [NOTIFICATION_TRIGGER_ID.companyJoinRequestCreated]: b(
      '',
      res,
      t('triggers.companyJoinRequestCreated.suffix', {
        company: String(p?.companyName ?? co ?? ''),
      }),
    ),
    // Informative — no action button, so this is a single sentence rather
    // than the bold-resource `b()` shape. Text differs by decision.
    [NOTIFICATION_TRIGGER_ID.companyJoinRequestDecided]: [
      {
        text:
          p?.decision === 'APPROVED'
            ? t('triggers.companyJoinRequestDecided.approved', {
                company: String(p?.companyName ?? co ?? ''),
              })
            : t('triggers.companyJoinRequestDecided.rejected', {
                company: String(p?.companyName ?? co ?? ''),
              }),
        bold: false,
      },
    ],
    [NOTIFICATION_TRIGGER_ID.auditRequested]: b(
      t('triggers.auditRequested.prefix', { co }),
      res,
    ),
    [NOTIFICATION_TRIGGER_ID.documentRequested]: b(
      t('triggers.documentRequested.prefix', { co }),
      res,
    ),
    [NOTIFICATION_TRIGGER_ID.questionnaireQuestionAdded]: b(
      t('triggers.questionnaireQuestionAdded.prefix', { co }),
      res,
    ),
    [NOTIFICATION_TRIGGER_ID.questionnaireInvitation]: b(
      t('triggers.questionnaireInvitation.prefix', { co }),
      res,
    ),
    [NOTIFICATION_TRIGGER_ID.questionnaireCommented]: b(
      t('triggers.questionnaireCommented.prefix', {
        co: String(p?.commenterUserName ?? co),
      }),
      res,
    ),
    [NOTIFICATION_TRIGGER_ID.questionnaireResponseSubmitted]: b(
      t('triggers.questionnaireResponseSubmitted.prefix', { co }),
      res,
    ),
    [NOTIFICATION_TRIGGER_ID.questionnaireReminder]: b(
      t('triggers.questionnaireReminder.prefix'),
      res,
    ),
    [NOTIFICATION_TRIGGER_ID.questionnaireUnarchived]: b(
      t('triggers.questionnaireUnarchived.prefix', { co }),
      res,
    ),
    [NOTIFICATION_TRIGGER_ID.questionnaireDueDateChanged]: b(
      t('triggers.questionnaireDueDateChanged.prefix'),
      res,
    ),
    [NOTIFICATION_TRIGGER_ID.questionnaireChangesRequested]: b(
      t('triggers.questionnaireChangesRequested.prefix', { co }),
      res,
    ),
    [NOTIFICATION_TRIGGER_ID.ownQuestionnaireDueDateClosing]: b(
      t('triggers.ownQuestionnaireDueDateClosing.prefix'),
      res,
      t('triggers.ownQuestionnaireDueDateClosing.suffix'),
    ),
    [NOTIFICATION_TRIGGER_ID.requestQuestionnaireDueDateClosing]: b(
      t('triggers.requestQuestionnaireDueDateClosing.prefix', { co }),
      res,
      t('triggers.requestQuestionnaireDueDateClosing.suffix'),
    ),
    [NOTIFICATION_TRIGGER_ID.supplyChainCollaborationRequested]: b(
      t('triggers.supplyChainCollaborationRequested.prefix', { co }),
      res,
    ),
    [NOTIFICATION_TRIGGER_ID.supplyChainCollaborationRequestReminded]: b(
      t('triggers.supplyChainCollaborationRequestReminded.prefix', { co }),
      res,
    ),
    [NOTIFICATION_TRIGGER_ID.supplyChainCollaborationSubmitted]: b(
      t('triggers.supplyChainCollaborationSubmitted.prefix', { co }),
      res,
      t('triggers.supplyChainCollaborationSubmitted.suffix'),
    ),
    [NOTIFICATION_TRIGGER_ID.supplyChainCollaborationChangesRequested]: b(
      t('triggers.supplyChainCollaborationChangesRequested.prefix', { co }),
      res,
    ),
    [NOTIFICATION_TRIGGER_ID.supplyChainCollaborationApproved]: b(
      t('triggers.supplyChainCollaborationApproved.prefix', { co }),
      res,
    ),
    [NOTIFICATION_TRIGGER_ID.cpmCollaboratorInvited]: cpm(
      'triggers.cpmCollaboratorInvited.prefix',
    ),
    [NOTIFICATION_TRIGGER_ID.cpmCollaboratorInReview]: cpm(
      'triggers.cpmCollaboratorInReview.prefix',
      'triggers.cpmCollaboratorInReview.suffix',
    ),
    [NOTIFICATION_TRIGGER_ID.cpmCollaboratorChangesRequested]: cpm(
      'triggers.cpmCollaboratorChangesRequested.prefix',
    ),
    [NOTIFICATION_TRIGGER_ID.cpmCollaboratorApproved]: cpm(
      'triggers.cpmCollaboratorApproved.prefix',
    ),
    [NOTIFICATION_TRIGGER_ID.cpmCollaboratorAccepted]: cpm(
      'triggers.cpmCollaboratorAccepted.prefix',
    ),
    [NOTIFICATION_TRIGGER_ID.cpmExportCompleted]: [
      { text: t('triggers.cpmExportCompleted'), bold: false },
    ],
    [NOTIFICATION_TRIGGER_ID.cpmExportFailed]: [
      { text: t('triggers.cpmExportFailed'), bold: false },
      ...(p?.errorMessage
        ? [{ text: ` ${String(p.errorMessage)}`, bold: false }]
        : []),
    ],
    // Informative, so there is no action button — the error report has to be
    // reachable from inside the sentence.
    // Informative, so the download is an inline link in the sentence rather
    // than a CTA — the same treatment as `cpmImportCompleted`.
    [NOTIFICATION_TRIGGER_ID.networkCompaniesExport]: [
      { text: t('triggers.networkCompaniesExport'), bold: false },
      ...(p?.fileUrl
        ? [
            {
              text: ` ${t('actionLabels.download')}`,
              bold: false,
              href: String(p.fileUrl),
            },
          ]
        : []),
    ],
    [NOTIFICATION_TRIGGER_ID.cpmImportCompleted]: [
      {
        text: t('triggers.cpmImportCompleted', {
          created: Number(p?.createdChains ?? 0),
          failed: Number(p?.totalErrors ?? p?.failedChains ?? 0),
        }),
        bold: false,
      },
      ...(p?.errorReportUrl
        ? [
            {
              text: ` ${t('actionLabels.downloadErrorReport')}`,
              bold: false,
              href: String(p.errorReportUrl),
            },
          ]
        : []),
    ],
    [NOTIFICATION_TRIGGER_ID.reportsTracingOperationsExport]: [
      { text: t('triggers.reportTracingOperationsExport'), bold: false },
    ],
    [NOTIFICATION_TRIGGER_ID.reportsCertificatesComplianceExport]: [
      { text: t('triggers.reportCertificatesComplianceExport'), bold: false },
    ],
    [NOTIFICATION_TRIGGER_ID.stylesExportCompleted]: [
      {
        text: t('triggers.stylesExportCompleted', {
          count: String(p?.totalRows ?? 0),
        }),
        bold: false,
      },
    ],
    [NOTIFICATION_TRIGGER_ID.stylesExportFailed]: [
      { text: t('triggers.stylesExportFailed'), bold: false },
    ],
    [NOTIFICATION_TRIGGER_ID.ordersExportCompleted]: [
      {
        text: t('triggers.ordersExportCompleted', {
          count: String(p?.totalRows ?? 0),
        }),
        bold: false,
      },
    ],
    [NOTIFICATION_TRIGGER_ID.ordersExportFailed]: [
      { text: t('triggers.ordersExportFailed'), bold: false },
    ],
    // The backend composes the full summary sentence in `payload.message`
    // (orders created/updated/failed + line counts); the fallback only fires if
    // it is ever missing.
    [NOTIFICATION_TRIGGER_ID.ordersBulkImportCompleted]: [
      {
        text: String(p?.message ?? t('triggers.ordersBulkImportCompleted')),
        bold: false,
      },
    ],
    [NOTIFICATION_TRIGGER_ID.stylesBulkImportCompleted]: [
      {
        text: String(p?.message ?? t('triggers.stylesBulkImportCompleted')),
        bold: false,
      },
    ],
    // Backend supplies a fully-built summary sentence in `payload.message`
    // (orders created/updated + error/warning counts); fall back to a generic
    // line only if it's ever missing (CMMM-345).
    [NOTIFICATION_TRIGGER_ID.certifiedMaterialsPoImportCompleted]: [
      {
        text: String(
          p?.message ?? t('triggers.certifiedMaterialsPoImportCompleted'),
        ),
        bold: false,
      },
    ],
    // `entity-scan-new-match` removed — see the note in `getActionLink`.
    [NOTIFICATION_TRIGGER_ID.entityScreeningKharonMatch]: [
      { text: t('triggers.entityScreeningKharonMatch'), bold: false },
    ],
    [NOTIFICATION_TRIGGER_ID.entityScreeningUflpaMatch]: [
      { text: t('triggers.entityScreeningUflpaMatch'), bold: false },
    ],
    [NOTIFICATION_TRIGGER_ID.capaUserAssigneeAdded]: b(
      t('triggers.capaUserAssigneeAdded.prefix', {
        co: String(p?.assignorCompanyName ?? co),
      }),
      String(p?.capaTitle ?? ''),
    ),
    [NOTIFICATION_TRIGGER_ID.capaCollaboratorAdded]: b(
      t('triggers.capaCollaboratorAdded.prefix', {
        co: String(p?.assignorCompanyName ?? co),
      }),
      String(p?.capaTitle ?? ''),
    ),
    [NOTIFICATION_TRIGGER_ID.capaResponsibleCompanyAdded]: b(
      t('triggers.capaResponsibleCompanyAdded.prefix', {
        co: String(p?.assignorCompanyName ?? co),
      }),
      String(p?.capaTitle ?? ''),
    ),
    [NOTIFICATION_TRIGGER_ID.capaOverdue]: b(
      t('triggers.capaOverdue.prefix'),
      String(p?.capaTitle ?? ''),
      t('triggers.capaOverdue.suffix'),
    ),
    [NOTIFICATION_TRIGGER_ID.capaStatusUpdateInReview]: b(
      t('triggers.capaStatusUpdateInReview.prefix', {
        co: String(p?.updaterCompanyName ?? co),
      }),
      String(p?.capaTitle ?? ''),
      t('triggers.capaStatusUpdateInReview.suffix'),
    ),
    [NOTIFICATION_TRIGGER_ID.capaStatusUpdateInReviewGeneral]: b(
      t('triggers.capaStatusUpdateInReview.prefix', {
        co: String(p?.updaterCompanyName ?? co),
      }),
      String(p?.capaTitle ?? ''),
      t('triggers.capaStatusUpdateInReview.suffix'),
    ),
    [NOTIFICATION_TRIGGER_ID.capaStatusUpdateChangesRequested]: b(
      t('triggers.capaStatusUpdateChangesRequested.prefix', {
        co: String(p?.updaterCompanyName ?? co),
      }),
      String(p?.capaTitle ?? ''),
    ),
    [NOTIFICATION_TRIGGER_ID.capaStatusUpdateChangesRequestedGeneral]: b(
      t('triggers.capaStatusUpdateChangesRequested.prefix', {
        co: String(p?.updaterCompanyName ?? co),
      }),
      String(p?.capaTitle ?? ''),
    ),
    [NOTIFICATION_TRIGGER_ID.capaStatusUpdateDoneGeneral]: b(
      t('triggers.capaStatusUpdateDoneGeneral.prefix', {
        co: String(p?.updaterCompanyName ?? co),
      }),
      String(p?.capaTitle ?? ''),
      t('triggers.capaStatusUpdateDoneGeneral.suffix'),
    ),
    [NOTIFICATION_TRIGGER_ID.capaRemediationAdded]: b(
      t('triggers.capaRemediationAdded.prefix', {
        co: String(p?.adderCompanyName ?? co),
      }),
      String(p?.capaTitle ?? ''),
    ),
    [NOTIFICATION_TRIGGER_ID.capaRemediationRemoved]: b(
      t('triggers.capaRemediationRemoved.prefix', {
        co: String(p?.removerCompanyName ?? co),
      }),
      String(p?.capaTitle ?? ''),
    ),
    [NOTIFICATION_TRIGGER_ID.capaCommentAdded]: b(
      t('triggers.capaCommentAdded.prefix', {
        co: String(p?.commentorCompanyName ?? co),
      }),
      String(p?.capaTitle ?? ''),
    ),
    // --- CAPA `_GENERAL` / `_REMOVED` variants -----------------------------
    // `_GENERAL` is the same event seen by a bystander (the reporter, another
    // collaborator, a watcher) rather than the person acted on, so the sentence
    // names the third party via `{{target}}` instead of saying "you". `b()`
    // builds exactly one bold run and the CAPA title is the more useful thing
    // to bold, so the target stays inside the prefix.
    //
    // Every payload field below was read off the handlers in
    // `backend/src/background-jobs/jobs/notifications/handlers/capa/`, not off
    // the old frontend — that one also reads `newCollaboratorUserName`, which
    // no handler has ever set.
    [NOTIFICATION_TRIGGER_ID.capaUserAssigneeAddedGeneral]: b(
      t('triggers.capaUserAssigneeAddedGeneral.prefix', {
        co: String(p?.assignorCompanyName ?? co),
        target: String(p?.assigneeCompanyName ?? ''),
      }),
      String(p?.capaTitle ?? ''),
    ),
    [NOTIFICATION_TRIGGER_ID.capaUserAssigneeRemoved]: b(
      t('triggers.capaUserAssigneeRemoved.prefix', {
        co: String(p?.assignorCompanyName ?? co),
      }),
      String(p?.capaTitle ?? ''),
    ),
    [NOTIFICATION_TRIGGER_ID.capaUserAssigneeRemovedGeneral]: b(
      t('triggers.capaUserAssigneeRemovedGeneral.prefix', {
        co: String(p?.assignorCompanyName ?? co),
        target: String(p?.assigneeCompanyName ?? ''),
      }),
      String(p?.capaTitle ?? ''),
    ),
    [NOTIFICATION_TRIGGER_ID.capaCollaboratorAddedGeneral]: b(
      t('triggers.capaCollaboratorAddedGeneral.prefix', {
        co: String(p?.assignorCompanyName ?? co),
        target: String(p?.newCollaboratorCompanyName ?? ''),
      }),
      String(p?.capaTitle ?? ''),
    ),
    [NOTIFICATION_TRIGGER_ID.capaCollaboratorRemoved]: b(
      t('triggers.capaCollaboratorRemoved.prefix', {
        co: String(p?.assignorCompanyName ?? co),
      }),
      String(p?.capaTitle ?? ''),
    ),
    [NOTIFICATION_TRIGGER_ID.capaCollaboratorRemovedGeneral]: b(
      t('triggers.capaCollaboratorRemovedGeneral.prefix', {
        co: String(p?.assignorCompanyName ?? co),
        target: String(p?.removedCollaboratorCompanyName ?? ''),
      }),
      String(p?.capaTitle ?? ''),
    ),
    [NOTIFICATION_TRIGGER_ID.capaStatusUpdateInProgressGeneral]: b(
      t('triggers.capaStatusUpdateInProgressGeneral.prefix', {
        co: String(p?.updaterCompanyName ?? co),
      }),
      String(p?.capaTitle ?? ''),
      t('triggers.capaStatusUpdateInProgressGeneral.suffix'),
    ),
    // Same copy as the direct variant — the payload is identical and the
    // sentence doesn't depend on the reader's role, exactly as
    // `capaStatusUpdateInReviewGeneral` reuses `capaStatusUpdateInReview`.
    [NOTIFICATION_TRIGGER_ID.capaCommentAddedGeneral]: b(
      t('triggers.capaCommentAdded.prefix', {
        co: String(p?.commentorCompanyName ?? co),
      }),
      String(p?.capaTitle ?? ''),
    ),
    // No `{{co}}`: on due-soon rows the creator is the recipient's own company,
    // so naming it would read "Acme reminded Acme". Shape mirrors `capaOverdue`.
    [NOTIFICATION_TRIGGER_ID.capaDueSoon]: b(
      t('triggers.capaDueSoon.prefix'),
      String(p?.capaTitle ?? ''),
      t('triggers.capaDueSoon.suffix'),
    ),
    [NOTIFICATION_TRIGGER_ID.capUserAssigneeAdded]: b(
      t('triggers.capUserAssigneeAdded.prefix', {
        co: String(p?.assignorCompanyName ?? co),
      }),
      String(p?.capTitle ?? ''),
    ),
    [NOTIFICATION_TRIGGER_ID.capCollaboratorAdded]: b(
      t('triggers.capCollaboratorAdded.prefix', {
        co: String(p?.assignorCompanyName ?? co),
      }),
      String(p?.capTitle ?? ''),
    ),
    [NOTIFICATION_TRIGGER_ID.capResponsibleCompanyAdded]: b(
      t('triggers.capResponsibleCompanyAdded.prefix', {
        co: String(p?.assignorCompanyName ?? co),
      }),
      String(p?.capTitle ?? ''),
    ),
    [NOTIFICATION_TRIGGER_ID.capOverdue]: b(
      t('triggers.capOverdue.prefix'),
      String(p?.capTitle ?? ''),
      t('triggers.capOverdue.suffix'),
    ),
    [NOTIFICATION_TRIGGER_ID.capCommentAdded]: b(
      t('triggers.capCommentAdded.prefix', {
        co: String(p?.commentorCompanyName ?? co),
      }),
      String(p?.capTitle ?? ''),
    ),
    // --- CAP `_GENERAL` / `_REMOVED` variants — mirrors of the CAPA set above.
    [NOTIFICATION_TRIGGER_ID.capUserAssigneeAddedGeneral]: b(
      t('triggers.capUserAssigneeAddedGeneral.prefix', {
        co: String(p?.assignorCompanyName ?? co),
        target: String(p?.assigneeCompanyName ?? ''),
      }),
      String(p?.capTitle ?? ''),
    ),
    [NOTIFICATION_TRIGGER_ID.capUserAssigneeRemoved]: b(
      t('triggers.capUserAssigneeRemoved.prefix', {
        co: String(p?.assignorCompanyName ?? co),
      }),
      String(p?.capTitle ?? ''),
    ),
    [NOTIFICATION_TRIGGER_ID.capUserAssigneeRemovedGeneral]: b(
      t('triggers.capUserAssigneeRemovedGeneral.prefix', {
        co: String(p?.assignorCompanyName ?? co),
        target: String(p?.assigneeCompanyName ?? ''),
      }),
      String(p?.capTitle ?? ''),
    ),
    [NOTIFICATION_TRIGGER_ID.capCollaboratorAddedGeneral]: b(
      t('triggers.capCollaboratorAddedGeneral.prefix', {
        co: String(p?.assignorCompanyName ?? co),
        target: String(p?.newCollaboratorCompanyName ?? ''),
      }),
      String(p?.capTitle ?? ''),
    ),
    [NOTIFICATION_TRIGGER_ID.capCollaboratorRemoved]: b(
      t('triggers.capCollaboratorRemoved.prefix', {
        co: String(p?.assignorCompanyName ?? co),
      }),
      String(p?.capTitle ?? ''),
    ),
    [NOTIFICATION_TRIGGER_ID.capCollaboratorRemovedGeneral]: b(
      t('triggers.capCollaboratorRemovedGeneral.prefix', {
        co: String(p?.assignorCompanyName ?? co),
        target: String(p?.removedCollaboratorCompanyName ?? ''),
      }),
      String(p?.capTitle ?? ''),
    ),
    [NOTIFICATION_TRIGGER_ID.capCommentAddedGeneral]: b(
      t('triggers.capCommentAdded.prefix', {
        co: String(p?.commentorCompanyName ?? co),
      }),
      String(p?.capTitle ?? ''),
    ),
    [NOTIFICATION_TRIGGER_ID.capDueSoon]: b(
      t('triggers.capDueSoon.prefix'),
      String(p?.capTitle ?? ''),
      t('triggers.capDueSoon.suffix'),
    ),
    // --- Non-CAPA triggers -------------------------------------------------
    [NOTIFICATION_TRIGGER_ID.documentShared]: b(
      t('triggers.documentShared.prefix', { co }),
      res,
    ),
    // The old frontend bolded both the company and the chain; `b()` builds one
    // bold run, and the chain name is what identifies the row.
    [NOTIFICATION_TRIGGER_ID.supplyChainCollaborationAccepted]: b(
      t('triggers.supplyChainCollaborationAccepted.prefix', { co }),
      res,
      t('triggers.supplyChainCollaborationAccepted.suffix'),
    ),
    // `res` is the company that linked and `co` is the same company, which is
    // why the old frontend never rendered the prefix either.
    [NOTIFICATION_TRIGGER_ID.companyLinked]: b(
      '',
      res,
      t('triggers.companyLinked.suffix'),
    ),
    // The old frontend omitted the questionnaire name; a feed listing several
    // questionnaires is unreadable without it, so v2 bolds it.
    [NOTIFICATION_TRIGGER_ID.questionnaireReviewCompleted]: b(
      t('triggers.questionnaireReviewCompleted.prefix', { co }),
      res,
    ),
    [NOTIFICATION_TRIGGER_ID.questionnaireArchived]: b(
      t('triggers.questionnaireArchived.prefix', { co }),
      res,
      t('triggers.questionnaireArchived.suffix'),
    ),
    [NOTIFICATION_TRIGGER_ID.nextPlannedAudit]: b(
      t('triggers.nextPlannedAudit.prefix'),
      res,
    ),
    [NOTIFICATION_TRIGGER_ID.slcpAssessmentSharedFeatureDisabled]: [
      {
        text: t('triggers.slcpAssessmentSharedFeatureDisabled', { co }),
        bold: false,
      },
    ],
    [NOTIFICATION_TRIGGER_ID.amforiSyncReport]: [
      { text: amforiSync(), bold: false },
    ],
    // The old frontend also `JSON.parse`d `uploadStatus.errors` to drive a
    // per-SKU error accordion. A feed row is one clamped sentence, so the
    // counts are the whole story — and an unguarded parse of a malformed
    // payload can no longer take the feed down.
    [NOTIFICATION_TRIGGER_ID.productsImported]: [
      {
        text: t('triggers.productsImported', {
          created: Number(upload.createdProducts ?? 0),
          updated: Number(upload.updatedProducts ?? 0),
          skus: Number(upload.attachedSKUs ?? 0),
          variations: Number(upload.updatedVariations ?? 0),
        }),
        bold: false,
      },
    ],
    // Informative, so the error report has to be an inline link rather than a
    // CTA — same shape as `cpmImportCompleted`. Unlike `stylesBulkImportCompleted`
    // this prefers the localised string over `payload.message`: the backend
    // builds that message in English only, and supplies both counts here, so
    // nothing is lost. It also sets `createdByCompanyName` to null, which is
    // why no part of this sentence uses `co`.
    [NOTIFICATION_TRIGGER_ID.bomBulkImportCompleted]: [
      {
        text: p?.hasSuccesses
          ? t('triggers.bomBulkImportCompleted', {
              successful: Number(p?.successfulCount ?? 0),
              failed: Number(p?.failedGroups ?? 0),
            })
          : t('triggers.bomBulkImportFailed'),
        bold: false,
      },
      ...(p?.errorReportUrl
        ? [
            {
              text: ` ${t('actionLabels.downloadErrorReport')}`,
              bold: false,
              href: String(p.errorReportUrl),
            },
          ]
        : []),
    ],
    [NOTIFICATION_TRIGGER_ID.certifiedMaterialsSupplierNewEvidenceRequested]: [
      {
        text: t('triggers.certifiedMaterialsNewEvidenceRequested', {
          co: String(p?.brandName ?? co),
          resource: res,
        }),
        bold: false,
      },
    ],
    [NOTIFICATION_TRIGGER_ID.certifiedMaterialsOrderSubmittedForReview]: [
      {
        text: t('triggers.certifiedMaterialsOrderSubmittedForReview', {
          co: String(p?.supplierName ?? co),
          resource: res,
        }),
        bold: false,
      },
    ],
    [NOTIFICATION_TRIGGER_ID.certifiedMaterialsDocumentRevisionRequested]: [
      {
        text: t('triggers.certifiedMaterialsDocumentRevisionRequested', {
          co: String(p?.clientName ?? co),
          resource: res,
        }),
        bold: false,
      },
    ],
    // `resourceName` is the order number for a single order and "N orders" for a
    // bulk assignment, so the sentence reads for both.
    [NOTIFICATION_TRIGGER_ID.certifiedMaterialsSupplierAssignedToOrders]: [
      {
        text: t('triggers.certifiedMaterialsSupplierAssignedToOrders', {
          co: String(p?.brandName ?? co),
          resource: res,
        }),
        bold: false,
      },
    ],
    [NOTIFICATION_TRIGGER_ID.certifiedMaterialsSupplierReminderSent]: [
      {
        text: t('triggers.certifiedMaterialsSupplierReminderSent', {
          co: String(p?.brandName ?? co),
          resource: res,
        }),
        bold: false,
      },
    ],
  };

  // The fallback deliberately omits `resourceName` — it exists only for ids this
  // app does not know about, and reaching it for a known trigger is a bug.
  return (
    map[trigger as NotificationTriggerId] ?? [
      { text: `${co} — ${trigger.replace(/-/g, ' ')}`, bold: false },
    ]
  );
}
